import React, { useEffect, useMemo, useState } from "react";
import {
    Badge,
    Button,
    Card,
    Dropdown,
    Form,
    Table,
    Row,
    Col,
    InputGroup,
    Modal,
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { Bounce, toast } from "react-toastify";
import { useTranslate } from "../hooks/LanguageProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    approveTransportRequestList,
    getTransportRequestList,
    getTransportRequestListCount,
    updateTransportRequestListStatus,
} from "../services/transportRequestList.service";
import {
    TransportRequestListItem,
    TransportRequestListSearchPayload,
} from "../types/transportRequestList.types";
import DetailsDrawer from "../components/TransportRequest/DetailsDrawer";
import { formatDateToTimestamp, } from "../functions";



type ColumnKey =
    | "id_transport_request"
    | "request_type"
    | "object_request"
    | "departure_location"
    | "arrival_location"
    | "requester_phone"
    | "status_request"
    | "created_at"
    | "actions";

interface ColumnOption {
    key: ColumnKey;
    label: string;
}

type TransportRequestAction = "approve" | "reject" | "cancel";

interface PendingAction {
    action: TransportRequestAction;
    request: TransportRequestListItem;
}

const ALL_COLUMNS: ColumnOption[] = [
    { key: "id_transport_request", label: "ID" },
    { key: "request_type", label: "Type" },
    { key: "object_request", label: "Object" },
    { key: "departure_location", label: "Departure" },
    { key: "arrival_location", label: "Arrival" },
    { key: "requester_phone", label: "Phone" },
    { key: "status_request", label: "Status" },
    { key: "created_at", label: "created_at" },
    { key: "actions", label: "Actions" },
];

const DEFAULT_SELECTED_COLUMNS: ColumnKey[] = [
    "id_transport_request",
    "request_type",
    "object_request",
    "departure_location",
    "arrival_location",
    "status_request",
    "actions",
];
export function TransportRequestList() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const id_user = localStorage.getItem("GeopUserID");

    const [requests, setRequests] = useState<TransportRequestListItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [search, setSearch] = useState<string>("");
    const [requestType, setRequestType] = useState<string>("all");

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [limitValue, setLimitValue] = useState<number>(10);
    const [pageCount, setPageCount] = useState<number>(1);
    const [total, setTotal] = useState<number>(0);

    const [sortColumn, setSortColumn] = useState<ColumnKey>(
        "id_transport_request",
    );
    const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

    const [selectedColumns, setSelectedColumns] = useState<ColumnKey[]>(
        DEFAULT_SELECTED_COLUMNS,
    );

    const [selectedRequest, setSelectedRequest] =
        useState<TransportRequestListItem | null>(null);
    const [showDrawer, setShowDrawer] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    const [isConfirmingAction, setIsConfirmingAction] = useState(false);

    const visibleColumns = useMemo(() => {
        return ALL_COLUMNS.filter((column) => selectedColumns.includes(column.key));
    }, [selectedColumns]);

    const { translate } = useTranslate();

    useEffect(() => {
        const mailDecision = searchParams.get("mailDecision");

        if (mailDecision !== "approved" && mailDecision !== "rejected") {
            return;
        }

        const alreadyDecided = searchParams.get("already_decided") === "1";
        const toastOptions = {
            position: "bottom-right" as const,
            autoClose: 3000,
            transition: Bounce,
        };

        if (mailDecision === "approved") {
            toast.success(
                alreadyDecided
                    ? "Cette demande a deja ete approuvee"
                    : "Demande approuvee depuis l'email",
                toastOptions,
            );
        } else {
            toast.warn(
                alreadyDecided
                    ? "Cette demande a deja ete traitee"
                    : "Demande rejetee depuis l'email",
                toastOptions,
            );
        }

        window.history.replaceState({}, document.title, window.location.pathname);
    }, [searchParams]);

    const loadTransportRequests = async (
        page = currentPage,
        searchValue = search,
        typeValue = requestType,
        limit = limitValue,
        column = sortColumn,
        sort = sortOrder,
    ) => {
        try {
            setLoading(true);

            const payload: TransportRequestListSearchPayload = {
                limitValue: limit,
                currentPage: page,
                id_user,
                search: searchValue,
                type: typeValue,
                colum: column,
                sort,
            };

            const [listResponse, countResponse] = await Promise.all([
                getTransportRequestList(payload),
                getTransportRequestListCount({
                    id_user,
                    search: searchValue,
                    type: typeValue,
                }),
            ]);

            const totalCount = Number(countResponse.count || 0);
            const computedPageCount = Math.max(1, Math.ceil(totalCount / limit));

            setRequests(listResponse);
            setTotal(totalCount);
            setPageCount(computedPageCount);

            if (page > computedPageCount) {
                setCurrentPage(1);
            }
        } catch (error: any) {
            console.error("Load transport requests error:", error);
            toast.error(error?.message || "Failed to load transport request list", {
                position: "bottom-right",
                autoClose: 2400,
                transition: Bounce,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTransportRequests();
    }, [currentPage, limitValue, sortColumn, sortOrder]);

    const handleSearch = async () => {
        setCurrentPage(1);
        await loadTransportRequests(
            1,
            search,
            requestType,
            limitValue,
            sortColumn,
            sortOrder,
        );
    };

    const handleClearSearch = async () => {
        setSearch("");
        setCurrentPage(1);
        await loadTransportRequests(
            1,
            "",
            requestType,
            limitValue,
            sortColumn,
            sortOrder,
        );
    };

    const handleRequestTypeChange = async (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const nextType = event.target.value;
        setRequestType(nextType);
        setCurrentPage(1);
        await loadTransportRequests(
            1,
            search,
            nextType,
            limitValue,
            sortColumn,
            sortOrder,
        );
    };

    const handlePageClick = (event: { selected: number }) => {
        setCurrentPage(event.selected + 1);
        window.scrollTo(0, 0);
    };

    const handleSort = (column: ColumnKey) => {
        if (column === "actions") return;

        if (sortColumn === column) {
            setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
            return;
        }

        setSortColumn(column);
        setSortOrder("ASC");
    };

    const handleToggleColumn = (columnKey: ColumnKey) => {
        if (columnKey === "actions") return;

        setSelectedColumns((prev) => {
            if (prev.includes(columnKey)) {
                return prev.filter((item) => item !== columnKey);
            }
            return [...prev, columnKey];
        });
    };

    const handleLimitChange = async (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const newLimit = Number(event.target.value);
        setLimitValue(newLimit);
        setCurrentPage(1);

        await loadTransportRequests(
            1,
            search,
            requestType,
            newLimit,
            sortColumn,
            sortOrder,
        );
    };

    const renderStatusBadge = (status: string) => {
        switch (status) {
            case "approved":
                return <Badge bg="success">Approved</Badge>;
            case "rejected":
                return <Badge bg="danger">Rejected</Badge>;
            case "cancelled":
                return <Badge bg="secondary">Cancelled</Badge>;
            case "mission_created":
                return <Badge bg="primary">Mission Created</Badge>;
            case "pending_fleet_processing":
                return (
                    <Badge
                        bg=""
                        style={{
                            backgroundColor: "#fbbf24",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: "11px",
                            padding: "6px 10px",
                            borderRadius: "4px",
                        }}
                    >
                        {translate("pending")}
                    </Badge>
                );
            default:
                return <Badge bg="info">Pending</Badge>;
        }
    };

    const openDrawer = (row: TransportRequestListItem) => {
        setSelectedRequest(row);
        setShowDrawer(true);
    };

    const closeDrawer = () => {
        setShowDrawer(false);
        setSelectedRequest(null);
    };

    const updateRequestStatus = (
        id: number,
        newStatus: "approved" | "rejected" | "cancelled" | "mission_created",
    ) => {
        setRequests((prev) =>
            prev.map((item) =>
                item.id_transport_request === id
                    ? { ...item, status_request: newStatus }
                    : item,
            ),
        );

        setSelectedRequest((prev) =>
            prev && prev.id_transport_request === id
                ? { ...prev, status_request: newStatus }
                : prev,
        );
    };

    const handleView = (row: TransportRequestListItem) => {
        openDrawer(row);
    };

    // const handleApprove = (row: TransportRequestListItem) => {
    //     updateRequestStatus(row.id_transport_request, "approved");

    //     toast.success(`Request #${row.id_transport_request} approved`, {
    //         position: "bottom-right",
    //         autoClose: 1800,
    //         transition: Bounce,
    //     });
    // };

    const requestConfirmation = (
        action: TransportRequestAction,
        request: TransportRequestListItem,
    ) => {
        setPendingAction({ action, request });
    };

    const closeConfirmation = () => {
        if (isConfirmingAction) return;
        setPendingAction(null);
    };

    const persistRequestStatus = async (
        row: TransportRequestListItem,
        status_request: "rejected" | "cancelled",
        approval_status: "rejected" | "cancelled",
    ) => {
        await updateTransportRequestListStatus({
            id_transport_request: row.id_transport_request,
            id_user,
            status_request,
            approval_status,
            approval_required: 0,
        });

        updateRequestStatus(row.id_transport_request, status_request);
    };

    const executeReject = async (row: TransportRequestListItem) => {
        await persistRequestStatus(row, "rejected", "rejected");

        toast.warn(`Request #${row.id_transport_request} rejected`, {
            position: "bottom-right",
            autoClose: 1800,
            transition: Bounce,
        });
    };

    const executeCancel = async (row: TransportRequestListItem) => {
        await persistRequestStatus(row, "cancelled", "cancelled");

        toast.warn(`Request #${row.id_transport_request} cancelled`, {
            position: "bottom-right",
            autoClose: 1800,
            transition: Bounce,
        });
    };

    const handleNewRequest = () => {
        navigate("/transport-request");
    };

    const executeApprove = async (row: TransportRequestListItem) => {
        try {
            const result = await approveTransportRequestList({
                id_transport_request: row.id_transport_request,
                id_user,
            });

            updateRequestStatus(row.id_transport_request, "mission_created");

            toast.success(result.message || "Mission created successfully", {
                position: "bottom-right",
                autoClose: 2000,
                transition: Bounce,
            });
        } catch (error: any) {
            console.error("Create mission on approve error:", error);

            toast.error(error?.message || "Failed to create mission", {
                position: "bottom-right",
                autoClose: 2400,
                transition: Bounce,
            });

            throw error;
        }
    };

    const confirmPendingAction = async () => {
        if (!pendingAction) return;

        try {
            setIsConfirmingAction(true);

            if (pendingAction.action === "approve") {
                await executeApprove(pendingAction.request);
            } else if (pendingAction.action === "reject") {
                await executeReject(pendingAction.request);
            } else {
                await executeCancel(pendingAction.request);
            }

            setPendingAction(null);
        } catch (error) {
            console.error("Confirm transport request action error:", error);
        } finally {
            setIsConfirmingAction(false);
        }
    };

    const handleApprove = (row: TransportRequestListItem) =>
        requestConfirmation("approve", row);

    const handleReject = (row: TransportRequestListItem) =>
        requestConfirmation("reject", row);

    const handleCancel = (row: TransportRequestListItem) =>
        requestConfirmation("cancel", row);

    const getConfirmationContent = () => {
        if (!pendingAction) {
            return {
                title: "",
                message: "",
                confirmLabel: translate("confirm"),
                variant: "primary",
            };
        }

        const requestLabel = `#${pendingAction.request.id_transport_request}`;

        switch (pendingAction.action) {
            case "approve":
                return {
                    title: translate("Confirm approval"),
                    message: `${translate("Approve request")} ${requestLabel} ? ${translate("A mission order will be created.")}`,
                    confirmLabel: translate("approve"),
                    variant: "success",
                };
            case "reject":
                return {
                    title: translate("Confirm rejection"),
                    message: `${translate("Reject request")} ${requestLabel} ?`,
                    confirmLabel: translate("reject"),
                    variant: "danger",
                };
            default:
                return {
                    title: translate("Confirm cancellation"),
                    message: `${translate("Cancel request")} ${requestLabel} ?`,
                    confirmLabel: translate("cancel"),
                    variant: "secondary",
                };
        }
    };

    const confirmationContent = getConfirmationContent();

    return (
        <div className="page-content">
            <Card className="shadow-sm border-0">
                <Card.Body className="p-4">
                    <div className="mb-4">
                        <div className="row align-items-center">
                            <div className="col-md-8">
                                <h4 className="mb-1 fw-bold">
                                    {translate("transport_requests")} : {requests?.length || 0}
                                </h4>

                                <p className="text-muted mb-1">
                                    {translate("request_validation_list_before_mission_creation")}
                                </p>
                            </div>

                            <div className="col-md-4 d-flex justify-content-md-end justify-content-start mt-3 mt-md-0">
                                <Button
                                    variant="success"
                                    onClick={handleNewRequest}
                                    style={{ height: "42px", minWidth: "160px" }}
                                    className="d-inline-flex align-items-center justify-content-center gap-2"
                                >
                                    <i className="las la-plus"></i>
                                    {translate("New Request")}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <Row className="g-2 align-items-center mb-3">
                        <Col xl={4} lg={4} md={12}>
                            <InputGroup>
                                <InputGroup.Text
                                    style={{
                                        height: "42px",
                                        minHeight: "42px",
                                        backgroundColor: "#fff",
                                        borderRight: "0",
                                    }}
                                >
                                    <i className="las la-search"></i>
                                </InputGroup.Text>

                                <Form.Control
                                    type="text"
                                    placeholder={translate("search") + "..."}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleSearch();
                                        }
                                    }}
                                    style={{
                                        height: "42px",
                                        minHeight: "42px",
                                        padding: "6px 12px",
                                        lineHeight: "1.2",
                                        borderLeft: "0",
                                        borderRight: search ? "0" : undefined,
                                        boxShadow: "none",
                                    }}
                                />

                                {search && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleClearSearch}
                                        style={{
                                            height: "42px",
                                            minHeight: "42px",
                                            borderLeft: "0",
                                        }}
                                        className="d-inline-flex align-items-center justify-content-center"
                                    >
                                        <i className="las la-times"></i>
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>

                        <Col xl={2} lg={3} md={4}>
                            <Form.Select
                                value={requestType}
                                onChange={handleRequestTypeChange}
                                style={{ height: "42px" }}
                            >
                                <option value="all">{translate("all")}</option>
                                <option value="Normal">{translate("Normal")}</option>
                                <option value="Urgent">{translate("Urgent")}</option>
                            </Form.Select>
                        </Col>

                        <Col xl={2} lg={2} md={4}>
                            <Button
                                variant="warning"
                                className="w-100 d-inline-flex align-items-center justify-content-center gap-2"
                                onClick={handleSearch}
                                disabled={loading}
                                style={{ height: "42px" }}
                            >
                                <i className="las la-search-plus"></i>
                                {translate("search")}
                            </Button>
                        </Col>

                        <Col xl={4} lg={4} md={4}>
                            <div className="d-flex align-items-center justify-content-lg-end gap-2 flex-wrap">
                                <span className="fw-medium text-dark">
                                    {translate("displaying")}
                                </span>

                                <Form.Select
                                    value={limitValue}
                                    onChange={handleLimitChange}
                                    style={{ width: "72px", height: "42px" }}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value="100">100</option>
                                    <option value="200">200</option>
                                    <option value="500">500</option>
                                </Form.Select>

                                <Dropdown align="end">
                                    <Dropdown.Toggle
                                        variant="warning"
                                        id="transport-columns-btn"
                                        style={{ height: "42px" }}
                                    >
                                        {translate("columns")}
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        {ALL_COLUMNS.map((column) => (
                                            <Dropdown.Item
                                                key={column.key}
                                                as="button"
                                                onClick={() => handleToggleColumn(column.key)}
                                                disabled={column.key === "actions"}
                                            >
                                                <Form.Check
                                                    type="checkbox"
                                                    label={translate(column.label)}
                                                    checked={selectedColumns.includes(column.key)}
                                                    readOnly
                                                />
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </Col>
                    </Row>

                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead style={{ backgroundColor: "#f8f9fc" }}>
                                <tr>
                                    {visibleColumns.map((column) => (
                                        <th
                                            key={column.key}
                                            className="fw-bold text-dark border-bottom"
                                            style={{
                                                cursor:
                                                    column.key !== "actions" ? "pointer" : "default",
                                                whiteSpace: "nowrap",
                                            }}
                                            onClick={() =>
                                                column.key !== "actions" && handleSort(column.key)
                                            }
                                        >
                                            {translate(column.label)}
                                            {sortColumn === column.key && (
                                                <span className="ms-2">
                                                    {sortOrder === "ASC" ? "▲" : "▼"}
                                                </span>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan={visibleColumns.length}
                                            className="text-center py-4"
                                        >
                                            {translate("loading")}...
                                        </td>
                                    </tr>
                                ) : requests.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={visibleColumns.length}
                                            className="text-center py-4"
                                        >
                                            {translate("no_transport_requests_found")}
                                        </td>
                                    </tr>
                                ) : (
                                    requests.map((row) => (
                                        <tr key={row.id_transport_request}>
                                            {visibleColumns.map((column) => {
                                                switch (column.key) {
                                                    case "id_transport_request":
                                                        return (
                                                            <td key={column.key}>
                                                                {row.id_transport_request}
                                                            </td>
                                                        );

                                                    case "request_type":
                                                        return (
                                                            <td key={column.key}>
                                                                {translate(row.request_type)}
                                                            </td>
                                                        );

                                                    case "object_request":
                                                        return (
                                                            <td key={column.key}>
                                                                {translate(row.object_request)}
                                                            </td>
                                                        );

                                                    case "departure_location":
                                                        return (
                                                            <td key={column.key}>
                                                                <div className="fw-medium">
                                                                    {translate(row.departure_location) || "-"}
                                                                </div>
                                                                <div className="text-muted small">
                                                                    {formatDateToTimestamp(
                                                                        row.departure_datetime || "-",
                                                                    )}
                                                                </div>
                                                            </td>
                                                        );

                                                    case "arrival_location":
                                                        return (
                                                            <td key={column.key}>
                                                                <div className="fw-medium">
                                                                    {row.arrival_location || "-"}
                                                                </div>
                                                                <div className="text-muted small">
                                                                    {formatDateToTimestamp(
                                                                        row.arrival_datetime || "-",
                                                                    )}
                                                                </div>
                                                            </td>
                                                        );
                                                    case "requester_phone":
                                                        return (
                                                            <td key={column.key}>{row.requester_phone}</td>
                                                        );

                                                    case "status_request":
                                                        return (
                                                            <td key={column.key}>
                                                                {renderStatusBadge(row.status_request)}
                                                            </td>
                                                        );

                                                    case "created_at":
                                                        return (
                                                            <td key={column.key}>
                                                                {formatDateToTimestamp(row.created_at || "") ||
                                                                    "-"}
                                                            </td>
                                                        );

                                                    case "actions":
                                                        return (
                                                            <td key={column.key}>
                                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-primary"
                                                                        onClick={() => handleView(row)}
                                                                    >
                                                                        {translate("view")}
                                                                    </Button>

                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-success"
                                                                        onClick={() => handleApprove(row)}
                                                                    >
                                                                        {translate("approve")}
                                                                    </Button>

                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-danger"
                                                                        onClick={() => handleReject(row)}
                                                                    >
                                                                        {translate("reject")}
                                                                    </Button>

                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-secondary"
                                                                        onClick={() => handleCancel(row)}
                                                                    >
                                                                        {translate("cancel")}
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        );

                                                    default:
                                                        return <td key={column.key}>-</td>;
                                                }
                                            })}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-4">
                        <div className="text-dark">
                            {translate("displaying")} {requests.length} {translate("on")}{" "}
                            {total} / {translate("Page")} {currentPage} {translate("on")}{" "}
                            {pageCount}
                        </div>

                        <ReactPaginate
                            previousLabel={translate("previous")}
                            nextLabel={translate("next")}
                            breakLabel={"..."}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={3}
                            onPageChange={handlePageClick}
                            forcePage={Math.max(currentPage - 1, 0)}
                            containerClassName={"pagination mb-0"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            breakClassName={"page-item"}
                            breakLinkClassName={"page-link"}
                            activeClassName={"active"}
                            disabledClassName={"disabled"}
                        />
                    </div>
                </Card.Body>
            </Card>

            <DetailsDrawer
                show={showDrawer}
                request={selectedRequest}
                onClose={closeDrawer}
                onApprove={handleApprove}
                onReject={handleReject}
                onCancel={handleCancel}
            />

            <Modal
                show={Boolean(pendingAction)}
                onHide={closeConfirmation}
                centered
                backdrop={isConfirmingAction ? "static" : true}
            >
                <Modal.Header closeButton={!isConfirmingAction}>
                    <Modal.Title>{confirmationContent.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">{confirmationContent.message}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="light"
                        onClick={closeConfirmation}
                        disabled={isConfirmingAction}
                    >
                        {translate("cancel")}
                    </Button>
                    <Button
                        variant={confirmationContent.variant}
                        onClick={confirmPendingAction}
                        disabled={isConfirmingAction}
                    >
                        {isConfirmingAction
                            ? translate("processing")
                            : confirmationContent.confirmLabel}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default TransportRequestList;
